import { Body, Heading, Icon } from '@assembly-js/design-system';
import { t } from 'i18next';
import { useMemo, useState } from 'react';

import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { getStudioPlanTitle } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import {
  getBaseTiersByPlan,
  SellableStudioPlanId,
  StudioPlanPricingState,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import { UpgradePlanModal } from 'src/routes/settings/plans/components/studio/UpgradePlanModal';
import { useGetStudioPlansQuery } from 'src/services/api/studioCreditApi';

// Freemium's upgrade target is Pro (the design's "Upgrade to Pro").
const TARGET_PLAN: SellableStudioPlanId = 'professional';

/**
 * Sidebar CTA that opens the Studio plan upgrade modal. SidebarNav renders it
 * only once onboarding is complete; here it self-gates to freemium Studio
 * pricing workspaces — the only ones with a plan to upgrade to. It also owns
 * the plans query so prices are already in hand when the modal opens.
 */
export function StudioUpgradePlanActionCard() {
  const { studio } = usePlanStatus();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isFreemiumStudioWorkspace =
    studio.isStudioPricingWorkspace &&
    !studio.isCurrentPlanLoading &&
    studio.studioPlan === 'freemium';

  // Skipped for workspaces that never see the CTA, so the request only fires
  // where an upgrade is actually offered.
  const { data, isError, refetch } = useGetStudioPlansQuery(undefined, {
    skip: !isFreemiumStudioWorkspace,
  });

  const pricing = useMemo<StudioPlanPricingState>(() => {
    const baseTiers = data ? getBaseTiersByPlan(data.plans) : undefined;
    if (baseTiers?.size) {
      return { status: 'ready', baseTiers };
    }
    // A catalog that priced nothing is as unusable as a failed request, so it
    // gets the same retry affordance rather than an empty modal.
    if (isError || baseTiers) {
      return {
        status: 'error',
        onRetry: () => {
          void refetch();
        },
      };
    }
    return { status: 'loading' };
  }, [data, isError, refetch]);

  if (!isFreemiumStudioWorkspace) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="mb-3 flex w-full appearance-none flex-row items-center gap-2 rounded border border-solid border-gray-150 bg-white py-1.5 pl-2 pr-3 shadow-none outline-none hover:cursor-pointer hover:bg-secondary-hover"
        onClick={() => setIsModalOpen(true)}
      >
        {/* Light-teal tile + dark gem, matching the Figma sidebar CTA. */}
        <div className="flex shrink-0 items-center justify-center rounded bg-[#bce7f4] p-[9px]">
          <Icon icon="gem" className="h-3.5 w-3.5 text-black-heading" />
        </div>
        <div className="flex w-full flex-col items-start">
          <Heading size="xs" className="text-black-heading">
            {t(($) => $.page_studio_plans.sidebar_upgrade.title, {
              plan: getStudioPlanTitle(TARGET_PLAN),
            })}
          </Heading>
          <Body className="text-[10px] leading-4 text-secondary">
            {t(($) => $.page_studio_plans.sidebar_upgrade.subtitle)}
          </Body>
        </div>
      </button>
      {/* Mounted only while open; the prices it renders are prefetched above. */}
      {isModalOpen && (
        <UpgradePlanModal
          isOpen
          onClose={() => setIsModalOpen(false)}
          pricing={pricing}
          initialPlanId={TARGET_PLAN}
        />
      )}
    </>
  );
}
